import { useState } from 'react';
import {
  Card, Flex, Text, Box, Heading,
  IconButton, Menu, Portal,
} from '@chakra-ui/react';
import { LuEllipsisVertical } from 'react-icons/lu';
import { useAuthContext } from '@contexts/auth';

import ConfirmDisableDialog from './ConfirmDisableDialog';
import type { TeamType } from '../types';
import CreatedBy from '@components/CreatedBy';
import UpdateDialog from './UpdateDialog';
import UpdateMembersDialog from './UpdateMembersDialog';
import UpdatePortfoliosDialog from './UpdatePortfoliosDialog';

type Props = {
  team: TeamType,
  refetch: () => void,
};

const TeamCard = ({ team, refetch }: Props) => {
  const [showConfirmDisableDialog, setShowConfirmDisableDialog] = useState(false);
  const [showUpdateMembersDialog, setShowUpdateMembersDialog] = useState(false);
  const [showUpdatePortfoliosDialog, setShowUpdatePortfoliosDialog] = useState(false);
  const [showUpdateDetailsDialog, setShowUpdateDetailsDialog] = useState(false);

  const { checkIfHasRole } = useAuthContext();

  const canManageTeams = checkIfHasRole('CAN_MANAGE_TEAMS');

  const isEnabled = team.is_enabled;

  return (
    <Card.Root
      size="sm"
      variant="elevated"
      bg="grayscale.fg"
      mt={4}
      px={4}
      py={3}
    >
      <Flex justifyContent="space-between" alignItems="center" gap={2}>
        <Box>
          <Heading
            size="md"
            textDecoration={isEnabled ? 'none' : 'line-through'}
            color={isEnabled ? 'fg.default' : 'fg.error'}
          >
            {team.name}
          </Heading>
          <Text color="fg.muted">
            {team.team_lead.name}
          </Text>
          <CreatedBy user={team.user} createdDate={team.created_date} />
        </Box>

        {canManageTeams && isEnabled && (
          <Flex alignItems="center">
            <Menu.Root>
              <Menu.Trigger asChild>
                <IconButton variant="ghost" size="sm">
                  <LuEllipsisVertical />
                </IconButton>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content>
                    <Menu.Item
                      value="editMembers"
                      onClick={() => setShowUpdateMembersDialog(true)}
                    >
                      Alterar membros
                    </Menu.Item>
                    <Menu.Item
                      value="editPortfolios"
                      onClick={() => setShowUpdatePortfoliosDialog(true)}
                    >
                      Alterar portifólios
                    </Menu.Item>
                    <Menu.Item
                      value="editDetails"
                      onClick={() => setShowUpdateDetailsDialog(true)}
                    >
                      Alterar detalhes
                    </Menu.Item>
                    {/* <Menu.Item
                      value="delete"
                      color="fg.error"
                      _hover={{ bg: "bg.error", color: "fg.error" }}
                      onClick={() => setShowConfirmDisableDialog(true)}
                    >
                      Desabilitar
                    </Menu.Item> */}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>     
          </Flex>
        )}
      </Flex>

      {showUpdateDetailsDialog && (
        <UpdateDialog
          team={team}
          handleClose={() => setShowUpdateDetailsDialog(false)}
          afterSubmit={() => {
            refetch();
            setShowUpdateDetailsDialog(false);
          }}
        />
      )}
      {showUpdateMembersDialog && (
        <UpdateMembersDialog
          team={team}
          handleClose={() => setShowUpdateMembersDialog(false)}
          afterSubmit={() => {
            refetch();
            setShowUpdateMembersDialog(false);
          }}
        />
      )}
      {showUpdatePortfoliosDialog && (
        <UpdatePortfoliosDialog
          team={team}
          handleClose={() => setShowUpdatePortfoliosDialog(false)}
          afterSubmit={() => {
            refetch();
            setShowUpdatePortfoliosDialog(false);
          }}
        />
      )}
      {showConfirmDisableDialog && (
        <ConfirmDisableDialog
          team={team}
          handleClose={() => setShowConfirmDisableDialog(false)}
          afterSubmit={() => {
            refetch();
            setShowConfirmDisableDialog(false);
          }}
        />
      )}
    </Card.Root>
  );
};

export default TeamCard;
